import React, { useCallback, useEffect, useRef, useState } from "react"
import { HomeCaseStudiesCarouselWrapper } from "./HomeCaseStudiesCarousel.styles"
import CaseStudy from "../../CaseStudies/CaseStudy/CaseStudy"
import ArrowLeft from "../../../icons/phosphor/regular/ArrowLeft"
import ArrowRight from "../../../icons/phosphor/regular/ArrowRight"
import { graphql, useStaticQuery } from "gatsby"
import { statamicAssetToFixed } from "../../../utils/statamicAssets"

/**
 * @returns {{
 *   caseStudiesTitle: string,
 *   caseStudies: {
 *     title: string,
 *     quote: string,
 *     name: string,
 *     jobTitleCompany: string | null,
 *     stats: [{
 *       value: string,
 *       label: string
 *     }],
 *     logo: {
 *       fixed: {
 *         src: string,
 *         width: number,
 *         height: number
 *       },
 *       title: string
 *     } | null
 *     image: {
 *       fixed: {
 *         src: string,
 *         width: number,
 *         height: number
 *       },
 *       title: string
 *     } | null
 *   }[]
 * } | null}
 */
function useCaseStudies(){
  const query = useStaticQuery(graphql`
    query HomeCaseStudiesQuery {
      statamic {
        entries(collection: "home", limit: 1) {
          data {
            ... on Statamic_Entry_Home_Home {
              case_studies_title
              case_studies {
                ... on Statamic_Entry_CaseStudies_CaseStudy {
                  title
                  quote
                  name
                  job_title_company
                  stats {
                    value
                    label
                  }
                  logo {
                    url
                    permalink
                    ... on Statamic_Asset_Assets {
                      alt
                      width
                      height
                    }
                  }
                  image {
                    url
                    permalink
                    ... on Statamic_Asset_Assets {
                      alt
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  const node = query?.statamic?.entries?.data?.[0]
  if(!node) return null
  if(!node.case_studies?.length) return null

  return {
    caseStudiesTitle: node.case_studies_title || null,
    caseStudies: node.case_studies.map(cs => ({
      title: cs.title,
      quote: cs.quote || "",
      name: cs.name,
      jobTitleCompany: cs.job_title_company || null,
      stats: cs.stats || [],
      logo: statamicAssetToFixed(cs.logo, { w: 240, h: 90, fit: "contain" }, { width: 240, height: 90 }),
      image: statamicAssetToFixed(cs.image, { w: 600, q: 90 }, {}),
    })),
  };
}

/**
 * HomeCaseStudiesCarousel - A responsive, accessible carousel component
 * Features:
 * - Navigation with prev/next buttons
 * - Keyboard navigation with arrow keys
 * - Pointer events for swipe support on both touch devices and mouse
 * - Slides move immediately when swiping begins and snap to the relevant slide when swiping ends
 * - Limited swipe (10% max) when at first/last slide to provide boundary feedback
 * - Accessibility support with ARIA attributes
 * - Visual feedback during swipe interactions
 *
 * Implementation notes:
 * - Uses pointer events instead of touch events to support both touch and mouse interactions
 * - Implements pointer capture to track movement even when pointer leaves the element
 * - Provides visual feedback with cursor changes and real-time movement
 * - Limits swipe to 10% when at first or last slide to indicate boundaries
 * - Maintains all accessibility features including keyboard navigation and ARIA attributes
 */
export default function HomeCaseStudiesCarousel(){

  const home = useCaseStudies();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [pointerStart, setPointerStart] = useState(null);
  const [pointerEnd, setPointerEnd] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const trackRef = useRef(null);
  const caseStudies = home?.caseStudies || [];
  const totalSlides = caseStudies.length;
  const slideRef = useRef();

  const isPrevDisabled = currentIndex === 0;
  const isNextDisabled = currentIndex === totalSlides - 1;

  const goToSlide = useCallback((index) => {
    // Ensure index is within bounds (no looping)
    const newIndex = Math.max(0, Math.min(index, totalSlides - 1));
    setCurrentIndex(newIndex);
  }, [totalSlides]);

  const goToPrevSlide = useCallback(() => goToSlide(currentIndex - 1), [currentIndex, goToSlide]);
  const goToNextSlide = useCallback(() => goToSlide(currentIndex + 1), [currentIndex, goToSlide]);

  // Real-time swipe implementation:
  // 1. Track pointer position with pointerStart and pointerEnd
  // 2. Calculate swipe distance as percentage of container width
  // 3. Apply this percentage as a transform offset during swiping
  // 4. When pointer is released, determine whether to change slides based on swipe distance

  const handlePointerDown = (e) => {
    // Only handle primary pointer (ignore multi-touch or right-clicks)
    if(e.button !== 0) return;

    // Don't initiate swipe on interactive elements
    if(e.target.closest('a, button, input, select, textarea, [role="button"]')) return;

    // Prevent default browser behavior (like image dragging)
    e.preventDefault();

    // Capture the pointer to receive events even when pointer leaves the element
    if(trackRef.current){
      trackRef.current.setPointerCapture(e.pointerId);
    }

    setPointerEnd(null);
    setPointerStart(e.clientX);
    setIsSwiping(true); // Disables CSS transition for immediate movement
    setSwipeOffset(0);  // Reset any previous offset
  };

  const handlePointerMove = (e) => {
    // Only process if we're in a swiping state
    if(!pointerStart || !isSwiping) return;

    // Prevent default browser behavior during swiping
    e.preventDefault();

    const currentPointer = e.clientX;
    setPointerEnd(currentPointer);

    // Calculate how far the user has swiped in pixels
    const diff = pointerStart - currentPointer;

    // Convert to percentage of container width for the transform
    // This makes the slide move in real-time with the pointer
    const containerWidth = slideRef.current?.offsetWidth || 1;
    let offsetPercentage = (diff / containerWidth) * 100;

    // Limit swipe to 10% when at boundaries (first/last slide)
    if((isPrevDisabled && diff < 0) || (isNextDisabled && diff > 0)){
      // If trying to swipe beyond the boundary, limit to 35%
      offsetPercentage = Math.min(Math.abs(offsetPercentage), 35) * Math.sign(offsetPercentage);
    }

    // Update the swipe offset which is applied to the transform in the render
    setSwipeOffset(offsetPercentage);
  };

  const handlePointerUp = (e) => {
    // Release pointer capture
    if(trackRef.current){
      trackRef.current.releasePointerCapture(e.pointerId);
    }

    if(!pointerStart || !pointerEnd){
      setIsSwiping(false);
      setSwipeOffset(0);
      return;
    }

    const distance = pointerStart - pointerEnd;
    const containerWidth = slideRef.current?.offsetWidth || 1;

    // Calculate what percentage of the slide width was swiped
    const swipePercentage = Math.abs(distance) / containerWidth;

    // Snap behavior:
    // - If user swiped more than 20% of the slide width, move to next/prev slide
    // - Otherwise, snap back to the current slide
    // - This creates a natural feel where small swipes bounce back
    // - And larger, intentional swipes complete the navigation
    if(swipePercentage > 0.2){
      if(distance > 0 && !isNextDisabled){
        goToNextSlide();
      }else if(distance < 0 && !isPrevDisabled){
        goToPrevSlide();
      }
    }

    // Reset all pointer-related states
    // This allows the CSS transition to take over for the snap animation
    setPointerStart(null);
    setPointerEnd(null);
    setIsSwiping(false); // Re-enables CSS transition
    setSwipeOffset(0);   // Removes any offset so the slide can snap to position
  };

  // Handle pointer cancel (e.g., when the pointer is captured by the browser UI)
  const handlePointerCancel = () => {
    setIsSwiping(false);
    setSwipeOffset(0);
    setPointerStart(null);
    setPointerEnd(null);
  };

  // Handle keyboard navigation
  useEffect(() => {

    const handleKeyDown = (e) => {
      if(e.key === 'ArrowLeft' && !isPrevDisabled) goToPrevSlide();
      else if(e.key === 'ArrowRight' && !isNextDisabled) goToNextSlide();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToPrevSlide, goToNextSlide, isPrevDisabled, isNextDisabled]);

  if(!home) return null;

  return (
      <HomeCaseStudiesCarouselWrapper className="px-3">
        <div className="container">
          {home.caseStudiesTitle && <h2>{home.caseStudiesTitle}</h2>}

          <div
              className="carousel-container"
              ref={slideRef}
              aria-roledescription="carousel"
              aria-label="Customer testimonials carousel"
          >
            <div className="carousel-controls">
              <button
                  className="carousel-button prev"
                  onClick={goToPrevSlide}
                  aria-label="Previous"
                  disabled={isPrevDisabled}
              >
                <ArrowLeft />
              </button>

              <button
                  className="carousel-button next"
                  onClick={goToNextSlide}
                  aria-label="Next"
                  disabled={isNextDisabled}
              >
                <ArrowRight />
              </button>
            </div>
            <div
                ref={trackRef}
                className={`carousel-track ${isSwiping ? 'is-swiping' : ''}`}
                style={{
                  transform: `translateX(calc(-${currentIndex * 100}% - ${swipeOffset}%))`
                }}
                aria-live="polite"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
            >
              {caseStudies.map((caseStudy, index) => (
                  <div
                      className="carousel-slide"
                      key={index}
                      aria-hidden={currentIndex !== index}
                      aria-roledescription="slide"
                      aria-label={`${index + 1} of ${totalSlides}`}
                  >
                    <CaseStudy caseStudy={caseStudy} alt={index % 2} />
                  </div>
              ))}
            </div>
          </div>
        </div>
      </HomeCaseStudiesCarouselWrapper>
  )
}